import { initializePaddle, type PaddleEventData } from '@paddle/paddle-js';
import { copyFor, paddleLocaleFor, type SupportedLocale } from './i18n';
import type { PublicCheckoutConfig } from './model';

const en = {
  eyebrow: 'PhotoKeeper · Secure checkout',
  detail: 'Complete payment here. Your license updates automatically after payment is confirmed.',
  binding: 'This order belongs to the account you selected in PhotoKeeper. Closing this window keeps the order available to resume.',
  totals: 'Your final total, currency and applicable taxes appear in the payment form below.',
  loading: 'Loading payment form…', ready: 'Your payment is securely processed by Paddle.',
  completed: 'Payment submitted. PhotoKeeper is confirming your license. This window closes automatically after confirmation.',
  closed: 'Payment has not completed. You can continue this order or return to PhotoKeeper.',
  error: 'The payment form could not complete. Check your connection and payment details, then retry the same order.',
  retry: 'Reload this order', back: 'Return to PhotoKeeper',
  refund: 'Refunds: within 168 hours, request a full refund in the app without giving a reason. From 168 to 360 hours, provide a reason and issue description for review. After 360 hours, contact support.',
};
const zh: typeof en = {
  eyebrow: 'PhotoKeeper · 安全支付',
  detail: '在这里完成付款，确认成功后自动更新授权。',
  binding: '此订单属于你在 PhotoKeeper 中选择的账号。关闭窗口后，仍可继续同一笔订单。',
  totals: '实际金额、币种和适用税费以以下支付表单为准。',
  loading: '正在加载支付表单…', ready: '支付由 Paddle 安全处理。',
  completed: '付款已提交，PhotoKeeper 正在确认授权。确认后将自动关闭此窗口。',
  closed: '付款尚未完成。你可以继续此订单，或返回 PhotoKeeper。',
  error: '支付暂时未能完成，请检查网络和付款信息后重试同一笔订单。',
  retry: '重新加载此订单', back: '返回 PhotoKeeper',
  refund: '退款规则：付款后 168 小时内，可在 App 中申请无理由全额退款；超过 168 小时且不超过 360 小时，需填写理由和问题描述并等待审核；超过 360 小时请联系客服。',
};

export async function startDesktopCheckout(
  container: HTMLElement, config: PublicCheckoutConfig, locale: SupportedLocale,
): Promise<void> {
  if (!config.checkout) throw new Error('missing_desktop_transaction');
  const transactionId = config.checkout.transactionId;
  const copy = locale === 'zh-CN' ? zh : en;
  const shared = copyFor(locale);
  container.replaceChildren();
  const shell = node('main', 'desktop-checkout');
  const header = node('header', 'desktop-checkout-header');
  header.append(node('p', 'eyebrow', copy.eyebrow), node('h1', '', shared.offers[config.checkout.offerId].name),
    node('p', '', copy.detail));
  if (config.environment === 'sandbox') header.append(node('span', 'sandbox-badge', shared.sandboxBadge));
  const status = node('p', 'desktop-checkout-status', copy.loading);
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  const frame = node('div', 'desktop-payment-frame');
  const actions = node('div', 'desktop-checkout-actions');
  const retry = node('button', 'checkout-button', copy.retry);
  retry.type = 'button'; retry.hidden = true;
  retry.addEventListener('click', () => window.location.reload());
  const back = node('button', 'desktop-checkout-back', copy.back);
  back.type = 'button'; back.addEventListener('click', () => window.close());
  actions.append(retry, back);
  shell.append(header, node('p', 'desktop-checkout-note', copy.binding),
    node('p', 'desktop-checkout-note', copy.totals), status, frame, actions,
    node('p', 'desktop-checkout-policy', copy.refund));
  container.append(shell);

  let completed = false;
  const showError = () => {
    if (completed) return;
    status.textContent = copy.error; status.classList.add('is-error'); retry.hidden = false;
  };
  const loadingTimeout = window.setTimeout(showError, 20_000);
  window.addEventListener('pagehide', () => window.clearTimeout(loadingTimeout), { once: true });
  function eventCallback(event: PaddleEventData): void {
    if (event.data?.transaction_id && event.data.transaction_id !== transactionId) return;
    if (event.name === 'checkout.completed') {
      if (event.data?.transaction_id !== transactionId) return;
      completed = true;
      window.clearTimeout(loadingTimeout);
      status.textContent = copy.completed; status.classList.remove('is-error'); retry.hidden = true;
      // This is display-only. The signed activation poll in the app authorizes the license.
    } else if (!completed && event.name === 'checkout.loaded') {
      window.clearTimeout(loadingTimeout);
      status.textContent = copy.ready; status.classList.remove('is-error'); retry.hidden = true;
    } else if (event.name === 'checkout.error' || event.name === 'checkout.payment.error') {
      window.clearTimeout(loadingTimeout); showError();
    } else if (!completed && event.name === 'checkout.closed') {
      window.clearTimeout(loadingTimeout);
      status.textContent = copy.closed; retry.hidden = false;
    }
  }

  try {
    const paddle = await initializePaddle({
      token: config.clientToken, environment: config.environment, eventCallback,
      checkout: { settings: {
        displayMode: 'inline', variant: 'one-page', locale: paddleLocaleFor(locale),
        frameTarget: 'desktop-payment-frame', frameInitialHeight: 450,
        frameStyle: 'width: 100%; min-width: 312px; border: none; background-color: transparent;',
      } },
    });
    if (!paddle) throw new Error('paddle_initialization_failed');
    paddle.Checkout.open({ transactionId });
  } catch {
    window.clearTimeout(loadingTimeout); showError();
  }
}

export function renderDesktopFailure(container: HTMLElement, locale: SupportedLocale, message: string): void {
  const copy = locale === 'zh-CN' ? zh : en;
  const shell = node('main', 'desktop-checkout');
  const actions = node('div', 'desktop-checkout-actions');
  const retry = node('button', 'checkout-button', copy.retry);
  retry.addEventListener('click', () => window.location.reload());
  const back = node('button', 'desktop-checkout-back', copy.back);
  back.addEventListener('click', () => window.close());
  actions.append(retry, back);
  const status = node('p', 'desktop-checkout-status is-error', message);
  status.setAttribute('role', 'alert');
  shell.append(node('h1', '', 'PhotoKeeper'), status, actions);
  container.replaceChildren(shell);
}

function node<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  element.className = className;
  if (text) element.textContent = text;
  return element;
}
